import * as React from "react"
import {useCallback, useState, useEffect, useRef} from "react"
import {Box, Grid, Typography} from "@mui/material";
import {ButtonLoadingSeeker, CardSeeker, IconSeeker} from "@ui-seeker/core";
import {useAppToast} from "@ui-seeker/app";
import {postSubmit} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {useUserApp} from "@hooks";
import {MatchersLoadingSkuBlock} from "../../@components";
import useRawMatcherTableItems from "../@hooks/useTableItems";
import RawMatcherTableRow from "./TableRow";
import RawMatcherImageComparisonModal from "../Options/ImageComparisonModal";


export default function RawMatcherTableView({ onEditItem, exposeRemoveItem }) {
	const { username } = useUserApp()
	const { showToast } = useAppToast()
	const { items, isLoading, total, hasMore, loadMore, removeItem } = useRawMatcherTableItems()
	const [validatingItems, setValidatingItems] = useState(new Set())
	const scrollContainerRef = useRef(null)

	// Exponer removeItem al componente padre
	useEffect(() => {
		if (exposeRemoveItem) {
			exposeRemoveItem(removeItem)
		}
	}, [removeItem, exposeRemoveItem])

	const isItemValidating = useCallback((mappingID) => {
		return validatingItems.has(mappingID)
	}, [validatingItems])

	const setItemValidating = useCallback((mappingID, isValidating) => {
		setValidatingItems(prev => {
			const newSet = new Set(prev)
			if (isValidating) {
				newSet.add(mappingID)
			} else {
				newSet.delete(mappingID)
			}
			return newSet
		})
	}, [])

	const scrollToTop = useCallback(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({
				top: 0,
				behavior: 'smooth'
			})
		}
	}, [])

	const handleValidate = useCallback((item) => {
		if (!item.aiCandidate) return

		setItemValidating(item.mappingID, true)
		scrollToTop()

		const skuID = item.aiCandidate.skuID

		// Save raw mapping (product to SKU)
		postSubmit(`${BACKEND_APIS.MATCHERS}/raw/mapping-product`, {
			skuID: skuID,
			mappingID: item.mappingID,
			siteID: item.siteID,
			productID: item.productID,
			brandID: item.brandID,
			notFound: false,
			qa: true
		}).then(() => {
			showToast.success(`Product ${item.productID} mapped successfully!`, {
				position: "top-right"
			})
			removeItem(item.mappingID)
			setItemValidating(item.mappingID, false)
		}).catch(e => {
			console.error('ERROR MAPPING PRODUCT', e)
			showToast.error(`Failed to map Product ${item.productID}`, {
				position: "top-left"
			})
			setItemValidating(item.mappingID, false)
		})
	}, [username, removeItem, showToast, scrollToTop])

	const handleNotFound = useCallback((item) => {
		setItemValidating(item.mappingID, true)
		scrollToTop()

		postSubmit(`${BACKEND_APIS.MATCHERS}/raw/mapping-product`, {
			skuID: null,
			mappingID: item.mappingID,
			siteID: item.siteID,
			productID: item.productID,
			brandID: item.brandID,
			notFound: true,
			qa: true
		}).then(() => {
			showToast.info(`Product ${item.productID} marked as not found`, {
				position: "top-right"
			})
			removeItem(item.mappingID)
			setItemValidating(item.mappingID, false)
		}).catch(e => {
			console.error('ERROR MARKING PRODUCT AS NOT FOUND', e)
			showToast.error(`Failed to mark Product ${item.productID} as not found`, {
				position: "top-left"
			})
			setItemValidating(item.mappingID, false)
		})
	}, [removeItem, showToast, scrollToTop])

	const handleEdit = useCallback((item) => {
		if (onEditItem) {
			onEditItem(item)
		}
	}, [onEditItem])

	if (isLoading && items.length === 0) {
		return <CardSeeker>
			<MatchersLoadingSkuBlock />
		</CardSeeker>
	}

	return <Box>
		<CardSeeker sx={{ mb: 1, px: 2, py: 1 }}>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Box display="flex" gap={2} alignItems="center">
					<Typography variant="h6" fontWeight="bold">
						Batch Mapping View
					</Typography>
					<Typography variant="body2" color="text.secondary">
						({items.length} / {total} items)
					</Typography>
				</Box>
				<Box display="flex" gap={1} alignItems="center">
					<IconSeeker icon="table-list" sx={{ fontSize: "1.2rem", color: 'primary.main' }} />
				</Box>
			</Box>
		</CardSeeker>

		{/* Column Headers */}
		<Grid container spacing={2} px={2} mb={1}>
			<Grid item xs={6}>
				<Typography variant="h6" color="primary" fontWeight="bold">
					Product to Map
				</Typography>
			</Grid>
			<Grid item xs={6}>
				<Typography variant="h6" color="success.main" fontWeight="bold">
					AI Candidate (SKU)
				</Typography>
			</Grid>
		</Grid>

		<Box ref={scrollContainerRef} sx={{ maxHeight: "75vh", overflowY: "auto", pr: 1 }}>
			{items.map(item => (
				<RawMatcherTableRow
					key={item.mappingID}
					item={item}
					onValidate={handleValidate}
					onNotFound={handleNotFound}
					onEdit={handleEdit}
					isValidating={isItemValidating(item.mappingID)}
				/>
			))}

			{hasMore && (
				<Box display="flex" justifyContent="center" mt={2} mb={2}>
					<ButtonLoadingSeeker
						loading={isLoading}
						onClick={loadMore}
						startIcon={<IconSeeker icon="arrow-down" />}
					>
						Load More ({total - items.length} remaining)
					</ButtonLoadingSeeker>
				</Box>
			)}

			{!hasMore && items.length > 0 && (
				<Box display="flex" justifyContent="center" mt={2} mb={2}>
					<Typography variant="body2" color="text.secondary">
						All items loaded
					</Typography>
				</Box>
			)}

			{items.length === 0 && !isLoading && (
				<CardSeeker>
					<Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
						<IconSeeker icon="circle-check" sx={{ fontSize: "4rem", color: 'success.main' }} />
						<Typography variant="h5">
							No pending mappings
						</Typography>
						<Typography variant="body2" color="text.secondary">
							All items have been processed
						</Typography>
					</Box>
				</CardSeeker>
			)}
		</Box>

		<RawMatcherImageComparisonModal
			brandOptions={[]}
			setSelectedSKU={() => {}}
			saveMapping={null}
		/>
	</Box>
}
